import API, { API_BASE_URL } from "../../services/api";
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Card,
  Row,
  Col,
  Button,
  Badge,
  Nav,
  ListGroup,
  Breadcrumb,
  Alert,
  Modal,
  Table,
  Pagination,
  Form,
  InputGroup,
  ProgressBar,
} from "react-bootstrap";
import {
  FaArrowLeft,
  FaBook,
  FaPlay,
  FaFilePdf,
  FaFolder,
  FaFolderOpen,
  FaVideo,
  FaDownload,
  FaEye,
  FaFileAlt,
  FaCreditCard,
  FaHome,
  FaLayerGroup,
  FaCertificate,
  FaGlobe,
  FaUsers,
  FaYoutube,
  FaSearch,
  FaChartLine,
  FaFileArchive,
  FaClipboardList,
  FaQuestionCircle,
  FaClock,
  FaTh,
  FaList,
  FaChevronDown,
  FaChevronUp,
  FaLaptopCode,
  FaBriefcase,
} from "react-icons/fa";
import { toast } from "react-toastify";
import Header from "../../components/common/Header";
import CourseService from "../../services/courseService";
import StudentService from "../../services/studentService";
import StudentBatchService from "../../services/studentBatchService";
import AssignmentService from "../../services/assignmentService";
import TestService from "../../services/testService";
import PaymentModal from "./PaymentModal";
import StudentReceiptView from "./ReceiptView";
import BatchMaterialsTab from "./components/BatchMaterialsTab";
import StudentCertificateTab from "./components/StudentCertificateTab";
import "../../styles/CourseView.css";

const CourseView = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  // Course state
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");

  // Assignments & Tests state
  const [assignments, setAssignments] = useState([]);
  const [tests, setTests] = useState([]);
  const [loadingAssignments, setLoadingAssignments] = useState(false);
  const [loadingTests, setLoadingTests] = useState(false);

  // Course Materials state
  const [batchId, setBatchId] = useState(null);
  const [isOnlineStudent, setIsOnlineStudent] = useState(false);
  const [hasBatch, setHasBatch] = useState(false);
  const [enabledCourseMaterials, setEnabledCourseMaterials] = useState([]);
  const [currentFolder, setCurrentFolder] = useState(null);
  const [folderPath, setFolderPath] = useState([]);
  const [loadingMaterials, setLoadingMaterials] = useState(false);

  // Student Progress state
  const [studentProgress, setStudentProgress] = useState({
    auto_progress: 0,
    manual_progress: 0,
    progress: 0,
    total_videos: 0,
    watched_videos: 0,
    status: "active",
  });
  const [loadingProgress, setLoadingProgress] = useState(false);

  // Overview state
  const [isDescExpanded, setIsDescExpanded] = useState(false);

  // Pagination states
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 20,
    total: 0,
    total_pages: 1,
  });
  const [itemsPerPage, setItemsPerPage] = useState(20);

  // Materials View Mode state (grid or list)
  const [materialsViewMode, setMaterialsViewMode] = useState("grid");

  // Search state
  const [searchTerm, setSearchTerm] = useState("");
  const [searchInput, setSearchInput] = useState("");
  
  const [testsSearchQuery, setTestsSearchQuery] = useState("");
  const [assignmentsSearchQuery, setAssignmentsSearchQuery] = useState("");

  // Folder counts state
  const [folderCounts, setFolderCounts] = useState({});

  // YouTube Modal state
  const [showYoutubeModal, setShowYoutubeModal] = useState(false);
  const [currentYoutubeVideo, setCurrentYoutubeVideo] = useState(null);

  // Payment state
  const [enrollment, setEnrollment] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);

  // Batch state
  const [batchInfo, setBatchInfo] = useState(null);
  const [loadingBatch, setLoadingBatch] = useState(false);

  // UPDATED: Helper functions for discounted fee (hidden from student)
  const getEffectiveFee = () => {
    if (!enrollment) return course?.price || 0;
    return enrollment.total_fees - (enrollment.discount_amount || 0);
  };

  const getDueAmount = () => {
    if (!enrollment) return 0;
    const effectiveFee = getEffectiveFee();
    return effectiveFee - (enrollment.paid_amount || 0);
  };

  const getProgress = () => {
    if (!enrollment) return 0;
    const effectiveFee = getEffectiveFee();
    if (effectiveFee <= 0) return 100;
    return Math.round(((enrollment.paid_amount || 0) / effectiveFee) * 100);
  };

  useEffect(() => {
    loadCourseData();
    checkStudentBatch();
  }, [id]);

  useEffect(() => {
    if (activeTab === "materials") {
      loadMaterials();
      loadStudentProgress();
    } else if (activeTab === "payments") {
      loadPaymentInfo();
    } else if (activeTab === "assignments" && assignments.length === 0) {
      loadAssignments();
    } else if (activeTab === "tests" && tests.length === 0) {
      loadTests();
    }
  }, [
    activeTab,
    currentFolder,
    batchId,
    isOnlineStudent,
    pagination.current_page,
    itemsPerPage,
    searchTerm,
  ]);

  const loadAssignments = async () => {
    try {
      setLoadingAssignments(true);
      const response = await AssignmentService.getMyAssignments();
      const allAssignments = response.data?.data || response.data || [];
      // Filter for this batch if batch exists, or by course if it has course_id
      const filtered = allAssignments.filter(a => 
        (batchInfo && a.assignment?.batch_id === batchInfo.id) || 
        (batchInfo && a.batch_id === batchInfo.id)
      );
      setAssignments(filtered);
    } catch (error) {
      console.error("Failed to load assignments:", error);
    } finally {
      setLoadingAssignments(false);
    }
  };

  const loadTests = async () => {
    try {
      setLoadingTests(true);
      const response = await TestService.getMyTests();
      const allTests = response.data?.data || response.data || [];
      // Filter by course_id or batch_id
      const filtered = allTests.filter(t => 
        t.course_id === parseInt(id) || 
        (batchInfo && t.test?.batch_id === batchInfo.id) || 
        (batchInfo && t.batch_id === batchInfo.id)
      );
      setTests(filtered);
    } catch (error) {
      console.error("Failed to load tests:", error);
    } finally {
      setLoadingTests(false);
    }
  };

  // Get YouTube video ID from URL
  const getYoutubeVideoId = (url) => {
    if (!url) return null;
    const regExp =
      /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
    const match = url.match(regExp);
    return match && match[2].length === 11 ? match[2] : null;
  };

  // Get YouTube thumbnail URL
  const getYoutubeThumbnail = (url) => {
    const videoId = getYoutubeVideoId(url);
    if (videoId) {
      return `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`;
    }
    return null;
  };

  // Track video watch progress
  const trackVideoWatch = async (materialId) => {
    if (hasBatch && batchId) {
      try {
        console.log("Tracking video watch:", { materialId, batchId });
        await StudentBatchService.markVideoWatched(materialId, batchId, 100);
        toast.success("Progress updated!");
        await loadStudentProgress();
      } catch (error) {
        console.error("Failed to track video:", error);
      }
    } else {
      console.log("No batch context, skipping video tracking");
    }
  };

  // Load student progress
  const loadStudentProgress = async () => {
    if (hasBatch && batchId) {
      try {
        setLoadingProgress(true);
        const response = await StudentBatchService.getStudentProgress(batchId);
        if (response.data) {
          setStudentProgress(response.data);
          console.log("Student progress loaded:", response.data);
        }
      } catch (error) {
        console.error("Failed to load student progress:", error);
      } finally {
        setLoadingProgress(false);
      }
    }
  };

  const loadCourseData = async () => {
    try {
      setLoading(true);
      const response = await CourseService.getCourseById(id);
      setCourse(response.data);
      await loadPaymentInfo();
    } catch (error) {
      toast.error("Failed to load course");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const checkStudentBatch = async () => {
    try {
      setLoadingBatch(true);
      const response = await StudentBatchService.getStudentBatchForCourse(id);

      if (response.data && response.data.has_batch) {
        setBatchInfo(response.data);
        setHasBatch(true);
        setIsOnlineStudent(false);
        setBatchId(response.data.batch.id);
        setTimeout(() => loadStudentProgress(), 500);
      } else if (response.data && response.data.is_online_student) {
        setHasBatch(false);
        setIsOnlineStudent(true);
        setBatchId(null);
      } else {
        setHasBatch(false);
        setIsOnlineStudent(false);
        setBatchId(null);
      }
    } catch (error) {
      console.error("Failed to check batch info:", error);
    } finally {
      setLoadingBatch(false);
    }
  };

  // Load folder count for a specific folder
  const loadFolderCount = async (folderId) => {
    try {
      let response;
      if (isOnlineStudent) {
        response = await StudentBatchService.getCourseFolderCount(folderId);
      } else if (hasBatch && batchId) {
        response = await StudentBatchService.getCourseFolderCount(
          folderId,
          batchId,
        );
      } else {
        return 0;
      }
      return response?.data?.count || 0;
    } catch (error) {
      console.error("Failed to load folder count:", error);
      return 0;
    }
  };

  const loadMaterials = async () => {
    try {
      setLoadingMaterials(true);

      let response;
      if (isOnlineStudent) {
        response = await StudentBatchService.getOnlineCourseMaterials(
          id,
          currentFolder,
          pagination.current_page,
          itemsPerPage,
          searchTerm,
        );
      } else if (hasBatch && batchId) {
        response = await StudentBatchService.getEnabledCourseMaterials(
          batchId,
          currentFolder,
          pagination.current_page,
          itemsPerPage,
          searchTerm,
        );
      } else {
        setEnabledCourseMaterials([]);
        setLoadingMaterials(false);
        return;
      }

      let materialsArray = [];
      let paginationData = {};

      if (response && response.data) {
        if (response.data.data && Array.isArray(response.data.data)) {
          materialsArray = response.data.data;
          paginationData = response.data.pagination || {};
        } else if (Array.isArray(response.data)) {
          materialsArray = response.data;
          paginationData = {};
        } else {
          materialsArray = [];
        }
      }

      setEnabledCourseMaterials(materialsArray);

      // Update pagination
      if (paginationData.total_pages) {
        setPagination({
          current_page: paginationData.current_page || 1,
          per_page: paginationData.per_page || itemsPerPage,
          total: paginationData.total || 0,
          total_pages: paginationData.total_pages || 1,
        });
      }

      // Load folder counts for folder items
      const folders = materialsArray.filter((item) => item.type === "folder");
      const counts = {};
      for (const folder of folders) {
        counts[folder.id] = await loadFolderCount(folder.id);
      }
      setFolderCounts(counts);

      if (currentFolder) {
        await loadFolderPath();
      } else {
        setFolderPath([]);
      }
    } catch (error) {
      console.error("Failed to load course materials:", error);
      setEnabledCourseMaterials([]);
      if (error.response?.status === 400) {
        toast.error("Please access materials through your batch");
      } else {
        toast.error("Failed to load course materials");
      }
    } finally {
      setLoadingMaterials(false);
    }
  };

  const loadFolderPath = async () => {
    if (!currentFolder) return;
    try {
      let response;
      if (isOnlineStudent) {
        response = await StudentBatchService.getOnlineFolderPath(
          currentFolder,
          id,
        );
      } else if (hasBatch && batchId) {
        response = await StudentBatchService.getCourseFolderPath(
          currentFolder,
          batchId,
        );
      }

      let pathArray = [];
      if (response && response.data) {
        if (Array.isArray(response.data)) {
          pathArray = response.data;
        } else if (response.data.data && Array.isArray(response.data.data)) {
          pathArray = response.data.data;
        } else if (response.data.path && Array.isArray(response.data.path)) {
          pathArray = response.data.path;
        } else {
          pathArray = [];
        }
      }
      setFolderPath(pathArray);
    } catch (error) {
      console.error("Failed to load folder path:", error);
      setFolderPath([]);
    }
  };

  const loadPaymentInfo = async () => {
    try {
      const studentId = JSON.parse(localStorage.getItem("user"))?.id;
      if (studentId) {
        const response = await StudentService.getStudentCourses(studentId);
        const courseEnrollment = (response.data || []).find(
          (c) => c.course_id === parseInt(id),
        );
        setEnrollment(courseEnrollment);

        const transResponse =
          await StudentService.getStudentTransactions(studentId);
        const courseTransactions = (transResponse.data || []).filter(
          (t) => t.course_id === parseInt(id),
        );
        setTransactions(courseTransactions);
      }
    } catch (error) {
      console.error("Failed to load payment info:", error);
    }
  };

  const handleFolderClick = (folder) => {
    setPagination((prev) => ({ ...prev, current_page: 1 }));
    setSearchTerm("");
    setSearchInput("");
    setCurrentFolder(folder.id);
  };

  const handleBreadcrumbClick = (index) => {
    setPagination((prev) => ({ ...prev, current_page: 1 }));
    setSearchTerm("");
    setSearchInput("");
    if (index === -1) {
      setCurrentFolder(null);
    } else if (folderPath && Array.isArray(folderPath) && folderPath[index]) {
      setCurrentFolder(folderPath[index].id);
    }
  };

  // Pagination handlers
  const handlePageChange = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= pagination.total_pages) {
      setPagination((prev) => ({ ...prev, current_page: pageNumber }));
    }
  };

  const handleItemsPerPageChange = (e) => {
    const newLimit = parseInt(e.target.value);
    setItemsPerPage(newLimit);
    setPagination((prev) => ({ ...prev, current_page: 1 }));
  };

  // Search handler
  const handleSearch = () => {
    setSearchTerm(searchInput);
    setPagination((prev) => ({ ...prev, current_page: 1 }));
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const handleClearSearch = () => {
    setSearchInput("");
    setSearchTerm("");
    setPagination((prev) => ({ ...prev, current_page: 1 }));
  };

  const renderPagination = () => {
    const { current_page, total_pages } = pagination;

    if (total_pages <= 1) return null;

    let items = [];
    const maxVisible = 5;
    let startPage = Math.max(1, current_page - Math.floor(maxVisible / 2));
    let endPage = Math.min(total_pages, startPage + maxVisible - 1);

    if (endPage - startPage + 1 < maxVisible) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    items.push(
      <Pagination.Prev
        key="prev"
        onClick={() => handlePageChange(current_page - 1)}
        disabled={current_page === 1}
      />,
    );

    if (startPage > 1) {
      items.push(
        <Pagination.Item key={1} onClick={() => handlePageChange(1)}>
          1
        </Pagination.Item>,
      );
      if (startPage > 2)
        items.push(<Pagination.Ellipsis key="ellipsis1" disabled />);
    }

    for (let i = startPage; i <= endPage; i++) {
      items.push(
        <Pagination.Item
          key={i}
          active={i === current_page}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </Pagination.Item>,
      );
    }

    if (endPage < total_pages) {
      if (endPage < total_pages - 1)
        items.push(<Pagination.Ellipsis key="ellipsis2" disabled />);
      items.push(
        <Pagination.Item
          key={total_pages}
          onClick={() => handlePageChange(total_pages)}
        >
          {total_pages}
        </Pagination.Item>,
      );
    }

    items.push(
      <Pagination.Next
        key="next"
        onClick={() => handlePageChange(current_page + 1)}
        disabled={current_page === total_pages}
      />,
    );

    return (
      <div className="d-flex justify-content-between align-items-center mt-4">
        <div className="d-flex align-items-center gap-2">
          <Form.Select
            size="sm"
            value={itemsPerPage}
            onChange={handleItemsPerPageChange}
            style={{ width: "auto" }}
          >
            <option value={10}>10 per page</option>
            <option value={20}>20 per page</option>
            <option value={50}>50 per page</option>
            <option value={100}>100 per page</option>
          </Form.Select>
          <span className="text-muted small">
            Showing {(pagination.current_page - 1) * itemsPerPage + 1} -{" "}
            {Math.min(pagination.current_page * itemsPerPage, pagination.total)}{" "}
            of {pagination.total} items
          </span>
        </div>
        <Pagination size="sm">{items}</Pagination>
      </div>
    );
  };

  const handleDownloadSyllabus = async () => {
    try {
      await CourseService.downloadSyllabus(id);
      toast.success("Syllabus download started");
    } catch (error) {
      toast.error("Failed to download syllabus");
    }
  };

  const handleViewFile = (file) => {
    const fileUrl = `${API_BASE_URL}/${file.file_path}`;
    window.open(fileUrl, "_blank");
  };
  const handlePlayYoutube = (item) => {
    setCurrentYoutubeVideo(item);
    setShowYoutubeModal(true);
  };

  const handlePaymentClick = () => {
    if (!enrollment) {
      toast.error("Enrollment information not found");
      return;
    }

    const dueAmount = getDueAmount();
    if (dueAmount <= 0) {
      toast.info("No payment due");
      return;
    }

    setShowPaymentModal(true);
  };

  const handlePaymentSuccess = () => {
    setShowPaymentModal(false);
    loadPaymentInfo();
    toast.success("Payment completed successfully");
  };

  const handleViewReceipt = (transaction) => {
    setSelectedTransaction(transaction);
    setShowReceiptModal(true);
  };

  const getIcon = (type, isExpanded = false) => {
    if (type === "folder") {
      return isExpanded ? (
        <FaFolderOpen className="text-warning me-2" size={20} />
      ) : (
        <FaFolder className="text-warning me-2" size={20} />
      );
    }
    if (type === "link") {
      return <FaYoutube className="text-danger me-2" size={20} />;
    }
    if (type === "zip") {
      return <FaFileArchive className="text-success me-2" size={20} />;
    }
    switch (type) {
      case "video":
        return <FaVideo className="text-primary me-2" size={20} />;
      case "pdf":
        return <FaFilePdf className="text-danger me-2" size={20} />;
      default:
        return <FaFileAlt className="text-secondary me-2" size={20} />;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
    }).format(amount || 0);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  // UPDATED: Use discounted values for display (no discount word shown)
  const effectiveFee = getEffectiveFee();
  const dueAmount = getDueAmount();
  const progress = getProgress();

  // Get progress bar color based on percentage
  const getProgressBarColor = (percentage) => {
    if (percentage >= 75) return "success";
    if (percentage >= 50) return "info";
    if (percentage >= 25) return "warning";
    return "danger";
  };

  const handleDownloadFile = async (file) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        toast.error("Please login to download");
        return;
      }

      // Get base URL from API config
      let baseURL = API.defaults.baseURL;
      baseURL = baseURL.replace(/\/backend$/, "");

      const downloadUrl = `${baseURL}/backend/materials/download/${file.id}`;

      console.log("Course Material Download URL:", downloadUrl);

      // Use fetch with Authorization header
      const response = await fetch(downloadUrl, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Download failed:", errorText);
        toast.error(`Download failed: ${response.status}`);
        return;
      }

      // Get blob from response
      const blob = await response.blob();

      // Get filename from Content-Disposition header if available
      let filename = file.name;
      const contentDisposition = response.headers.get("Content-Disposition");
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success("Download started!");
    } catch (error) {
      console.error("Download failed:", error);
      toast.error("Failed to download file");
    }
  };

  if (loading) {
    return (
      <div>
        <Header />
        <div className="course-view-loading">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (!course) {
    return (
      <div>
        <Header />
        <div className="course-view-not-found">
          <h3>Course not found</h3>
          <Button
            variant="primary"
            onClick={() => navigate("/student/courses")}
          >
            Back to Courses
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="course-view-modern">
      <Header />

      <div className="course-view-container">
        {/* Back Button */}
        <Button
          variant="link"
          className="course-view-back-btn"
          onClick={() => navigate("/student/courses")}
        >
          <FaArrowLeft className="me-2" /> Back to My Courses
        </Button>

        {/* Tabs Card */}
        <Card className="course-view-tabs-card">
          <Card.Header className="course-view-tabs-header p-0">
            <div className="tab-scroll-container">
              <div className="tab-scroll-wrapper">
                <Nav
                  variant="tabs"
                  activeKey={activeTab}
                  onSelect={(k) => setActiveTab(k)}
                  className="w-100 px-3 pt-3"
                >
                  <Nav.Item>
                    <Nav.Link eventKey="overview" className="course-view-tab-link">
                      <FaBook className="me-2" /> Overview
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="materials" className="course-view-tab-link">
                      <FaFolder className="me-2" /> Course Materials
                    </Nav.Link>
                  </Nav.Item>
                  {hasBatch && (
                    <Nav.Item>
                      <Nav.Link
                        eventKey="batch-materials"
                        className="course-view-tab-link"
                      >
                        <FaLayerGroup className="me-2" /> Batch Materials
                      </Nav.Link>
                    </Nav.Item>
                  )}
                  {hasBatch && (
                    <Nav.Item>
                      <Nav.Link
                        eventKey="assignments"
                        className="course-view-tab-link"
                      >
                        <FaClipboardList className="me-2" /> Assignments
                      </Nav.Link>
                    </Nav.Item>
                  )}
                  {hasBatch && (
                    <Nav.Item>
                      <Nav.Link
                        eventKey="tests"
                        className="course-view-tab-link"
                      >
                        <FaQuestionCircle className="me-2" /> Tests
                      </Nav.Link>
                    </Nav.Item>
                  )}
                  {hasBatch && (
                    <Nav.Item>
                      <Nav.Link
                        eventKey="certificate"
                        className="course-view-tab-link"
                      >
                        <FaCertificate className="me-2" /> Certificate
                      </Nav.Link>
                    </Nav.Item>
                  )}
                </Nav>
              </div>
            </div>
          </Card.Header>

          <Card.Body className="course-view-tab-content">
            {/* Overview Tab */}
            {activeTab === "overview" && (
              <div>
                <Row>
                  <Col md={8}>
                    <h5 className="section-title">About This Course</h5>
                    <div className="course-description-container">
                      <p className={`section-text ${!isDescExpanded ? 'clamped-text' : ''}`}>
                        {course.description}
                      </p>
                      {course.description && course.description.length > 150 && (
                        <Button 
                          variant="link" 
                          className="read-more-btn p-0"
                          onClick={() => setIsDescExpanded(!isDescExpanded)}
                        >
                          {isDescExpanded ? (
                            <>Show Less <FaChevronUp className="ms-1" size={12}/></>
                          ) : (
                            <>Read More <FaChevronDown className="ms-1" size={12}/></>
                          )}
                        </Button>
                      )}
                    </div>

                    <h5 className="section-title mt-5">What You'll Learn</h5>
                    <Row className="learn-grid mt-3">
                      <Col sm={6} className="mb-3">
                        <div className="learn-grid-item d-flex align-items-start">
                          <div className="icon-circle bg-primary-soft me-3">
                            <FaBook className="text-primary" />
                          </div>
                          <div>
                            <h6 className="mb-1">Comprehensive Knowledge</h6>
                            <small className="text-muted">Deep understanding of {course.name}</small>
                          </div>
                        </div>
                      </Col>
                      <Col sm={6} className="mb-3">
                        <div className="learn-grid-item d-flex align-items-start">
                          <div className="icon-circle bg-success-soft me-3">
                            <FaLaptopCode className="text-success" />
                          </div>
                          <div>
                            <h6 className="mb-1">Practical Skills</h6>
                            <small className="text-muted">Hands-on projects and real exercises</small>
                          </div>
                        </div>
                      </Col>
                      <Col sm={6} className="mb-3">
                        <div className="learn-grid-item d-flex align-items-start">
                          <div className="icon-circle bg-info-soft me-3">
                            <FaBriefcase className="text-info" />
                          </div>
                          <div>
                            <h6 className="mb-1">Industry Ready</h6>
                            <small className="text-muted">Real-world applications and workflows</small>
                          </div>
                        </div>
                      </Col>
                      <Col sm={6} className="mb-3">
                        <div className="learn-grid-item d-flex align-items-start">
                          <div className="icon-circle bg-warning-soft me-3">
                            <FaCertificate className="text-warning" />
                          </div>
                          <div>
                            <h6 className="mb-1">Certification</h6>
                            <small className="text-muted">Earn a certificate upon completion</small>
                          </div>
                        </div>
                      </Col>
                    </Row>

                    {hasBatch && batchInfo?.batch && (
                      <Card className="batch-info-card mt-4">
                        <Card.Body>
                          <h6 className="batch-info-title">
                            Batch Information
                          </h6>
                          <hr className="batch-info-divider" />
                          <p>
                            <strong>Batch:</strong> {batchInfo.batch.name}
                          </p>
                          <p>
                            <strong>Batch Progress:</strong>{" "}
                            {studentProgress.progress || 0}%
                          </p>
                          <p>
                            <strong>Batch Status:</strong>{" "}
                            {studentProgress.status === "completed" ? (
                              <Badge bg="success">Completed</Badge>
                            ) : (
                              <Badge bg="primary">In Progress</Badge>
                            )}
                          </p>
                        </Card.Body>
                      </Card>
                    )}

                    {isOnlineStudent && (
                      <Card className="online-benefits-card mt-4">
                        <Card.Body>
                          <h6 className="online-benefits-title">
                            Online Student Benefits
                          </h6>
                          <hr className="online-benefits-divider" />
                          <p>
                            <FaGlobe className="benefit-icon" /> Full access to
                            all course materials
                          </p>
                          <p>
                            <FaPlay className="benefit-icon" /> Self-paced
                            learning
                          </p>
                          <p>
                            <FaDownload className="benefit-icon" /> Download all
                            resources
                          </p>
                          <p>
                            <FaCertificate className="benefit-icon" />{" "}
                            Certificate on completion
                          </p>
                        </Card.Body>
                      </Card>
                    )}
                  </Col>
                  <Col md={4}>
                    <Card className="course-details-card">
                      <Card.Body>
                        <h6 className="details-title">Course Details</h6>
                        <hr className="details-divider" />
                        <p className="details-item">
                          <strong>Duration:</strong> {course.duration}
                        </p>
                        <p className="details-item">
                          <strong>Course Fee:</strong>{" "}
                          {formatCurrency(effectiveFee)}
                        </p>
                        {course.syllabus_name && (
                          <Button
                            variant="outline-primary"
                            size="sm"
                            className="syllabus-btn w-100 mt-3"
                            onClick={handleDownloadSyllabus}
                          >
                            <FaFilePdf className="me-2" /> Download Syllabus
                          </Button>
                        )}
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              </div>
            )}

            {/* Materials Tab */}
            {activeTab === "materials" && (
              <div>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="materials-title mb-0">Course Materials</h5>
                  <div className="view-mode-toggle border rounded bg-white">
                    <Button 
                      variant={materialsViewMode === 'grid' ? 'primary' : 'light'} 
                      className="border-0 rounded-start"
                      onClick={() => setMaterialsViewMode('grid')}
                    >
                      <FaTh />
                    </Button>
                    <Button 
                      variant={materialsViewMode === 'list' ? 'primary' : 'light'} 
                      className="border-0 rounded-end"
                      onClick={() => setMaterialsViewMode('list')}
                    >
                      <FaList />
                    </Button>
                  </div>
                </div>

                {/* Student Progress Bar Section */}
                {hasBatch && batchId && (
                  <Card className="mb-4 student-progress-card">
                    <Card.Body>
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <h6 className="mb-0">
                          <FaChartLine className="me-2 text-primary" />
                          Your Learning Progress
                        </h6>
                        <Badge
                          bg={
                            studentProgress.progress >= 100
                              ? "success"
                              : "primary"
                          }
                          pill
                        >
                          {studentProgress.progress || 0}% Complete
                        </Badge>
                      </div>
                      <ProgressBar
                        now={studentProgress.progress || 0}
                        variant={getProgressBarColor(
                          studentProgress.progress || 0,
                        )}
                        className="mb-2"
                        style={{ height: "10px" }}
                      />
                      <div className="d-flex justify-content-between mt-2">
                        <small className="text-muted">
                          📹 Videos Watched:{" "}
                          {studentProgress.watched_videos || 0} /{" "}
                          {studentProgress.total_videos || 0}
                        </small>
                        <small className="text-muted">
                          ✋ Auto: {studentProgress.auto_progress || 0}% |
                          Manual: {studentProgress.manual_progress || 0}%
                        </small>
                      </div>
                      {studentProgress.progress >= 100 && (
                        <Alert variant="success" className="mt-3 mb-0 py-2">
                          <FaCertificate className="me-2" />
                          <strong>Congratulations!</strong> You have completed
                          this course. You can now download your certificate.
                        </Alert>
                      )}
                    </Card.Body>
                  </Card>
                )}

                {/* Beautiful Search Bar */}
                <div className="mb-4">
                  <InputGroup className="materials-search-bar shadow-sm rounded-pill overflow-hidden bg-white border">
                    <InputGroup.Text className="bg-transparent border-0 ps-4 text-muted">
                      <FaSearch />
                    </InputGroup.Text>
                    <Form.Control
                      placeholder="Search materials by title..."
                      value={searchInput}
                      onChange={(e) => setSearchInput(e.target.value)}
                      onKeyPress={handleSearchKeyPress}
                      className="border-0 py-2 shadow-none"
                      style={{ outline: "none", boxShadow: "none" }}
                    />
                    <Button variant="primary" className="px-4 border-0 rounded-pill m-1" onClick={handleSearch}>
                      Search
                    </Button>
                    {searchTerm && (
                      <Button variant="light" className="px-3 border-0 text-danger rounded-pill m-1" onClick={handleClearSearch}>
                        Clear
                      </Button>
                    )}
                  </InputGroup>
                </div>

                {isOnlineStudent ? (
                  <>
                    <Alert variant="success" className="materials-alert">
                      <FaGlobe className="me-2" />
                      <strong>Welcome Online Learner!</strong> You have full
                      access to all course materials.
                    </Alert>

                    <Breadcrumb className="materials-breadcrumb">
                      <Breadcrumb.Item
                        onClick={() => handleBreadcrumbClick(-1)}
                        active={!currentFolder}
                        style={{ cursor: "pointer" }}
                      >
                        <FaHome className="me-1" />
                        All Materials
                      </Breadcrumb.Item>
                      {folderPath &&
                        Array.isArray(folderPath) &&
                        folderPath.map((folder, index) => (
                          <Breadcrumb.Item
                            key={folder.id}
                            onClick={() => handleBreadcrumbClick(index)}
                            active={index === folderPath.length - 1}
                            style={{ cursor: "pointer" }}
                          >
                            {folder.name}
                          </Breadcrumb.Item>
                        ))}
                    </Breadcrumb>

                    {loadingMaterials ? (
                      <div className="text-center py-4">
                        <div
                          className="spinner-border text-primary"
                          size="sm"
                        />
                      </div>
                    ) : enabledCourseMaterials.length === 0 ? (
                      <Alert variant="info" className="materials-empty-alert">
                        <FaFolder className="me-2" />
                        {searchTerm
                          ? "No materials found matching your search."
                          : "No course materials available in this folder."}
                      </Alert>
                    ) : (
                      <>
                        <div className={`materials-container ${materialsViewMode === 'grid' ? 'materials-grid' : 'materials-list-view'}`}>
                          {enabledCourseMaterials.map((item) => {
                            const thumbnail =
                              item.type === "link"
                                ? getYoutubeThumbnail(item.url_link)
                                : null;
                            return (
                              <div
                                key={item.id}
                                className={`material-card ${materialsViewMode}-mode`}
                                style={{
                                  cursor:
                                    item.type === "folder"
                                      ? "pointer"
                                      : "default",
                                }}
                                onClick={() =>
                                  item.type === "folder" &&
                                  handleFolderClick(item)
                                }
                              >
                                <div className="material-card-content">
                                  {item.type === "link" && thumbnail ? (
                                    <div className="thumbnail-wrapper">
                                      <img
                                        src={thumbnail}
                                        alt={item.name}
                                        className="materials-thumbnail"
                                        onError={(e) => {
                                          e.target.style.display = "none";
                                        }}
                                      />
                                      <div className="play-overlay"><FaYoutube size={30}/></div>
                                    </div>
                                  ) : (
                                    <div className={`materials-icon icon-${item.type}`}>
                                      {getIcon(item.type)}
                                    </div>
                                  )}
                                  <div className="materials-info">
                                    <strong className="materials-name text-truncate d-block" title={item.name}>
                                      {item.name}
                                    </strong>
                                    {item.type === "folder" &&
                                      folderCounts[item.id] !== undefined && (
                                        <Badge bg="secondary" className="mt-1">
                                          {folderCounts[item.id]}{" "}
                                          {folderCounts[item.id] === 1
                                            ? "item"
                                            : "items"}
                                        </Badge>
                                      )}
                                    {item.type === "zip" && (
                                      <Badge bg="success" className="mt-1">
                                        📦 ZIP
                                      </Badge>
                                    )}
                                    {item.type !== "folder" &&
                                      item.type !== "link" &&
                                      item.type !== "zip" && (
                                        <small className="materials-size text-muted mt-1 d-block">
                                          {item.file_size
                                            ? `${(item.file_size / 1024).toFixed(2)} KB`
                                            : ""}
                                        </small>
                                      )}
                                  </div>
                                </div>

                                <div>
                                  {item.type !== "folder" &&
                                    (item.type === "link" ? (
                                      <Button
                                        variant="danger"
                                        size="sm"
                                        className="materials-watch-btn"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          trackVideoWatch(item.id);
                                          handlePlayYoutube(item);
                                        }}
                                      >
                                        <FaYoutube className="me-1" /> Watch
                                      </Button>
                                    ) : item.type === "video" ? (
                                      <Button
                                        variant="primary"
                                        size="sm"
                                        className="materials-view-btn"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          trackVideoWatch(item.id);
                                          handleViewFile(item);
                                        }}
                                      >
                                        <FaEye className="me-1" /> View
                                      </Button>
                                    ) : item.type === "zip" ? (
                                      <Button
                                        variant="success"
                                        size="sm"
                                        className="materials-download-btn"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleDownloadFile(item);
                                        }}
                                      >
                                        <FaDownload className="me-1" /> Download
                                      </Button>
                                    ) : (
                                      <Button
                                        variant="primary"
                                        size="sm"
                                        className="materials-view-btn"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleViewFile(item);
                                        }}
                                      >
                                        <FaEye className="me-1" /> View
                                      </Button>
                                    ))}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        {renderPagination()}
                      </>
                    )}
                  </>
                ) : hasBatch ? (
                  <>


                    <Breadcrumb className="materials-breadcrumb">
                      <Breadcrumb.Item
                        onClick={() => handleBreadcrumbClick(-1)}
                        active={!currentFolder}
                        style={{ cursor: "pointer" }}
                      >
                        <FaHome className="me-1" />
                        Enabled Materials
                      </Breadcrumb.Item>
                      {folderPath &&
                        Array.isArray(folderPath) &&
                        folderPath.map((folder, index) => (
                          <Breadcrumb.Item
                            key={folder.id}
                            onClick={() => handleBreadcrumbClick(index)}
                            active={index === folderPath.length - 1}
                            style={{ cursor: "pointer" }}
                          >
                            {folder.name}
                          </Breadcrumb.Item>
                        ))}
                    </Breadcrumb>

                    {loadingMaterials ? (
                      <div className="text-center py-4">
                        <div
                          className="spinner-border text-primary"
                          size="sm"
                        />
                      </div>
                    ) : enabledCourseMaterials.length === 0 ? (
                      <Alert
                        variant="warning"
                        className="materials-warning-alert"
                      >
                        <FaFolder className="me-2" />
                        {searchTerm
                          ? "No materials found matching your search."
                          : "No course materials have been enabled for your batch yet."}
                      </Alert>
                    ) : (
                      <>
                        <div className={`materials-container ${materialsViewMode === 'grid' ? 'materials-grid' : 'materials-list-view'}`}>
                          {enabledCourseMaterials.map((item) => {
                            const thumbnail =
                              item.type === "link"
                                ? getYoutubeThumbnail(item.url_link)
                                : null;
                            return (
                              <div
                                key={item.id}
                                className={`material-card ${materialsViewMode}-mode`}
                                style={{
                                  cursor:
                                    item.type === "folder"
                                      ? "pointer"
                                      : "default",
                                }}
                                onClick={() =>
                                  item.type === "folder" &&
                                  handleFolderClick(item)
                                }
                              >
                                <div className="material-card-content">
                                  {item.type === "link" && thumbnail ? (
                                    <div className="thumbnail-wrapper">
                                      <img
                                        src={thumbnail}
                                        alt={item.name}
                                        className="materials-thumbnail"
                                        onError={(e) => {
                                          e.target.style.display = "none";
                                        }}
                                      />
                                      <div className="play-overlay"><FaYoutube size={30}/></div>
                                    </div>
                                  ) : (
                                    <div className={`materials-icon icon-${item.type}`}>
                                      {getIcon(item.type)}
                                    </div>
                                  )}
                                  <div className="materials-info">
                                    <strong className="materials-name text-truncate d-block" title={item.name}>
                                      {item.name}
                                    </strong>
                                    {item.type === "folder" &&
                                      folderCounts[item.id] !== undefined && (
                                        <Badge bg="secondary" className="mt-1">
                                          {folderCounts[item.id]}{" "}
                                          {folderCounts[item.id] === 1
                                            ? "item"
                                            : "items"}
                                        </Badge>
                                      )}
                                    {item.type === "zip" && (
                                      <Badge bg="success" className="mt-1">
                                        📦 ZIP
                                      </Badge>
                                    )}
                                    {item.type !== "folder" &&
                                      item.type !== "link" &&
                                      item.type !== "zip" && (
                                        <small className="materials-size text-muted mt-1 d-block">
                                          {item.file_size
                                            ? `${(item.file_size / 1024).toFixed(2)} KB`
                                            : ""}
                                        </small>
                                      )}
                                  </div>
                                </div>

                                <div className="material-actions mt-2 mt-sm-0">
                                  {item.type !== "folder" &&
                                    (item.type === "link" ? (
                                      <Button
                                        variant="danger"
                                        size="sm"
                                        className="materials-watch-btn"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          trackVideoWatch(item.id);
                                          handlePlayYoutube(item);
                                        }}
                                      >
                                        <FaYoutube className="me-1" /> Watch
                                      </Button>
                                    ) : item.type === "video" ? (
                                      <Button
                                        variant="primary"
                                        size="sm"
                                        className="materials-view-btn"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          trackVideoWatch(item.id);
                                          handleViewFile(item);
                                        }}
                                      >
                                        <FaEye className="me-1" /> View
                                      </Button>
                                    ) : item.type === "zip" ? (
                                      <Button
                                        variant="success"
                                        size="sm"
                                        className="materials-download-btn"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleDownloadFile(item);
                                        }}
                                      >
                                        <FaFileArchive className="me-1" />{" "}
                                        Download ZIP
                                      </Button>
                                    ) : (
                                      <div className="d-flex gap-2 justify-content-start">
                                        <Button
                                          variant="primary"
                                          size="sm"
                                          className="materials-view-btn"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleViewFile(item);
                                          }}
                                        >
                                          <FaEye />
                                        </Button>
                                        <Button
                                          variant="outline-primary"
                                          size="sm"
                                          className="materials-download-btn"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleDownloadFile(item);
                                          }}
                                        >
                                          <FaDownload />
                                        </Button>
                                      </div>
                                    ))}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        {renderPagination()}
                      </>
                    )}
                  </>
                ) : (
                  <Alert variant="danger" className="materials-danger-alert">
                    <FaFolder className="me-2" />
                    You are not enrolled in any batch for this course.
                  </Alert>
                )}
              </div>
            )}

            {/* Batch Materials Tab */}
            {activeTab === "batch-materials" &&
              hasBatch &&
              batchInfo?.batch && (
                <BatchMaterialsTab
                  batchId={batchInfo.batch.id}
                  batchName={batchInfo.batch.name}
                />
              )}

            {/* Certificate Tab */}
            {activeTab === "certificate" && hasBatch && batchInfo && (
              <StudentCertificateTab
                batchInfo={batchInfo}
                courseName={course.name}
                studentName={JSON.parse(localStorage.getItem("user"))?.name}
              />
            )}

            {/* Payments Tab - UPDATED to use discounted values */}
            {activeTab === "payments" && enrollment && (
              <div>
                <Row>
                  <Col md={6}>
                    <Card className="payment-summary-card">
                      <Card.Body>
                        <h5 className="payment-summary-title">
                          Payment Summary
                        </h5>
                        <hr />
                        <div className="payment-summary-row">
                          <span>Total Fees:</span>
                          <strong>{formatCurrency(effectiveFee)}</strong>
                        </div>
                        <div className="payment-summary-row">
                          <span>Paid Amount:</span>
                          <strong className="text-success">
                            {formatCurrency(enrollment.paid_amount)}
                          </strong>
                        </div>
                        <div className="payment-summary-row">
                          <span>Due Amount:</span>
                          <strong className="text-danger">
                            {formatCurrency(dueAmount)}
                          </strong>
                        </div>
                        {dueAmount > 0 && (
                          <Button
                            variant="success"
                            className="payment-now-btn w-100 mt-3"
                            onClick={handlePaymentClick}
                          >
                            <FaCreditCard className="me-2" /> Make Payment (₹
                            {dueAmount})
                          </Button>
                        )}
                      </Card.Body>
                    </Card>
                  </Col>

                  <Col md={6}>
                    <Card className="transactions-card">
                      <Card.Body>
                        <h5 className="transactions-title">
                          Recent Transactions
                        </h5>
                        <hr />
                        {transactions.length === 0 ? (
                          <p className="text-muted text-center py-3">
                            No transactions yet
                          </p>
                        ) : (
                          <Table
                            size="sm"
                            striped
                            className="transactions-table"
                          >
                            <thead>
                              <tr>
                                <th>Date</th>
                                <th>Amount</th>
                                <th>Method</th>
                                <th>Receipt</th>
                              </tr>
                            </thead>
                            <tbody>
                              {transactions.slice(0, 5).map((t) => (
                                <tr key={t.id}>
                                  <td>{formatDate(t.payment_date)}</td>
                                  <td className="text-success">
                                    {formatCurrency(t.amount)}
                                  </td>
                                  <td>
                                    <Badge
                                      bg="secondary"
                                      className="text-uppercase"
                                    >
                                      {t.payment_method}
                                    </Badge>
                                  </td>
                                  <td>
                                    <Button
                                      variant="link"
                                      size="sm"
                                      onClick={() => handleViewReceipt(t)}
                                    >
                                      <FaFilePdf /> View
                                    </Button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        )}
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              </div>
            )}
            {/* Assignments Tab */}
            {activeTab === "assignments" && (
              <div className="course-assignments-tab">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="section-title mb-0">Assignments</h5>
                </div>

                {/* Beautiful Search Bar */}
                <div className="mb-4">
                  <InputGroup className="materials-search-bar shadow-sm rounded-pill overflow-hidden bg-white border">
                    <InputGroup.Text className="bg-transparent border-0 ps-4 text-muted">
                      <FaSearch />
                    </InputGroup.Text>
                    <Form.Control
                      placeholder="Search assignments by title..."
                      value={assignmentsSearchQuery}
                      onChange={(e) => setAssignmentsSearchQuery(e.target.value)}
                      className="border-0 py-2 shadow-none"
                      style={{ outline: "none", boxShadow: "none" }}
                    />
                    {assignmentsSearchQuery && (
                      <Button variant="light" className="px-3 border-0 text-danger rounded-pill m-1" onClick={() => setAssignmentsSearchQuery("")}>
                        Clear
                      </Button>
                    )}
                  </InputGroup>
                </div>

                {loadingAssignments ? (
                  <div className="text-center py-4">
                    <ProgressBar animated now={100} variant="primary" className="mb-2 mx-auto" style={{ maxWidth: '200px' }} />
                    <p className="text-muted">Loading assignments...</p>
                  </div>
                ) : assignments.filter(a => {
                    const title = (a.assignment || a).title || "";
                    return title.toLowerCase().includes(assignmentsSearchQuery.toLowerCase());
                  }).length === 0 ? (
                  <Alert variant="info" className="materials-alert-info">
                    <FaClipboardList className="me-2" /> 
                    {assignmentsSearchQuery ? "No assignments found matching your search." : "No assignments available for this course or batch yet."}
                  </Alert>
                ) : (
                  <Row className="g-4">
                    {assignments.filter(a => {
                      const title = (a.assignment || a).title || "";
                      return title.toLowerCase().includes(assignmentsSearchQuery.toLowerCase());
                    }).map(a => {
                      const assignment = a.assignment || a; // depending on structure
                      return (
                        <Col md={6} lg={4} key={assignment.id}>
                          <Card className="material-card h-100 border-0 shadow-sm">
                            <Card.Body className="d-flex flex-column">
                              <div className="d-flex align-items-center mb-3">
                                <div className="materials-icon icon-video me-3" style={{ width: '40px', height: '40px', fontSize: '1.2rem' }}>
                                  <FaClipboardList className="text-primary" />
                                </div>
                                <h6 className="mb-0 materials-name text-truncate" title={assignment.title}>
                                  {assignment.title}
                                </h6>
                              </div>
                              <div className="mt-auto">
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                  <small className="text-muted">
                                    <FaClock className="me-1" /> Due: {new Date(assignment.due_date).toLocaleDateString()}
                                  </small>
                                  <Badge bg={a.submission ? "success" : "warning"} pill>
                                    {a.submission ? "Submitted" : "Pending"}
                                  </Badge>
                                </div>
                                <Button 
                                  variant="primary" 
                                  className="w-100 rounded-pill"
                                  onClick={() => navigate(`/student/assignments/${assignment.id}`)}
                                >
                                  <FaEye className="me-1" /> View Assignment
                                </Button>
                              </div>
                            </Card.Body>
                          </Card>
                        </Col>
                      );
                    })}
                  </Row>
                )}
              </div>
            )}

            {/* Tests Tab */}
            {activeTab === "tests" && (
              <div className="course-tests-tab">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="section-title mb-0">Tests & Quizzes</h5>
                </div>

                {/* Beautiful Search Bar */}
                <div className="mb-4">
                  <InputGroup className="materials-search-bar shadow-sm rounded-pill overflow-hidden bg-white border">
                    <InputGroup.Text className="bg-transparent border-0 ps-4 text-muted">
                      <FaSearch />
                    </InputGroup.Text>
                    <Form.Control
                      placeholder="Search tests by title..."
                      value={testsSearchQuery}
                      onChange={(e) => setTestsSearchQuery(e.target.value)}
                      className="border-0 py-2 shadow-none"
                      style={{ outline: "none", boxShadow: "none" }}
                    />
                    {testsSearchQuery && (
                      <Button variant="light" className="px-3 border-0 text-danger rounded-pill m-1" onClick={() => setTestsSearchQuery("")}>
                        Clear
                      </Button>
                    )}
                  </InputGroup>
                </div>

                {loadingTests ? (
                  <div className="text-center py-4">
                    <ProgressBar animated now={100} variant="primary" className="mb-2 mx-auto" style={{ maxWidth: '200px' }} />
                    <p className="text-muted">Loading tests...</p>
                  </div>
                ) : tests.filter(test => {
                    return (test.title || "").toLowerCase().includes(testsSearchQuery.toLowerCase());
                  }).length === 0 ? (
                  <Alert variant="info" className="materials-alert-info">
                    <FaQuestionCircle className="me-2" /> 
                    {testsSearchQuery ? "No tests found matching your search." : "No tests available for this course or batch yet."}
                  </Alert>
                ) : (
                  <Row className="g-4">
                    {tests.filter(test => {
                      return (test.title || "").toLowerCase().includes(testsSearchQuery.toLowerCase());
                    }).map(test => (
                      <Col md={6} lg={4} key={test.id}>
                        <Card className="material-card h-100 border-0 shadow-sm">
                          <Card.Body className="d-flex flex-column">
                            <div className="d-flex align-items-center mb-3">
                              <div className="materials-icon icon-video me-3" style={{ width: '40px', height: '40px', fontSize: '1.2rem' }}>
                                <FaQuestionCircle className="text-primary" />
                              </div>
                              <h6 className="mb-0 materials-name text-truncate" title={test.title}>
                                {test.title}
                              </h6>
                            </div>
                            <div className="mt-auto">
                              <div className="d-flex flex-column mb-3">
                                <small className="text-muted mb-1">
                                  <FaClock className="me-1" /> Duration: {test.duration_minutes} mins
                                </small>
                                <small className="text-muted mb-2">
                                  <FaClipboardList className="me-1" /> Total Marks: {test.total_marks}
                                </small>
                                <div>
                                  <Badge bg={test.attempt_status === "completed" ? "success" : "warning"} pill>
                                    {test.attempt_status === "completed" ? "Completed" : "Pending"}
                                  </Badge>
                                </div>
                              </div>
                              
                              {test.attempt_status === "completed" ? (
                                <Button 
                                  variant="outline-success" 
                                  className="w-100 rounded-pill"
                                  onClick={() => navigate(`/student/test-result/${test.attempt_id}`)}
                                >
                                  View Result
                                </Button>
                              ) : (
                                <Button 
                                  variant="primary" 
                                  className="w-100 rounded-pill"
                                  onClick={() => navigate("/student/tests")}
                                >
                                  Go to Tests
                                </Button>
                              )}
                            </div>
                          </Card.Body>
                        </Card>
                      </Col>
                    ))}
                  </Row>
                )}
              </div>
            )}
          </Card.Body>
        </Card>

        {/* Payment Modal */}
        <PaymentModal
          show={showPaymentModal}
          onHide={() => setShowPaymentModal(false)}
          course={{
            ...enrollment,
            total_fees: effectiveFee,
            due_amount: dueAmount,
          }}
          studentId={JSON.parse(localStorage.getItem("user"))?.id}
          onSuccess={handlePaymentSuccess}
        />

        {/* Receipt Modal */}
        <StudentReceiptView
          show={showReceiptModal}
          onHide={() => {
            setShowReceiptModal(false);
            setSelectedTransaction(null);
          }}
          transaction={selectedTransaction}
        />

        {/* YouTube Player Modal */}
        <Modal
          show={showYoutubeModal}
          onHide={() => setShowYoutubeModal(false)}
          size="lg"
          centered
          className="youtube-modal"
        >
          <Modal.Header closeButton>
            <Modal.Title className="youtube-modal-title">
              <FaYoutube className="text-danger me-2" />
              {currentYoutubeVideo?.name}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-0">
            {currentYoutubeVideo && (
              <div className="youtube-embed-container">
                <iframe
                  src={`https://www.youtube.com/embed/${getYoutubeVideoId(currentYoutubeVideo.url_link)}?autoplay=1&rel=0&modestbranding=0&showinfo=0&controls=1`}
                  className="youtube-iframe"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  title={currentYoutubeVideo.name}
                />
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => setShowYoutubeModal(false)}
            >
              Close
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </div>
  );
};

export default CourseView;
